/**
 * useDocAI — Hook für KI-gestützte Dokumentenanalyse
 * 
 * Analysiert E-Mail-Anhänge und gibt Empfehlungen:
 * - Dokumenttyp (Rechnung, Kürzungsschreiben, Klageerwiderung, etc.)
 * - Stellungnahme erforderlich?
 * - Aktenverknüpfung (AZ, Kennzeichen, Schadennummer)
 * - Ablagevorschlag (Firma → Ordner → Monat ODER Akte → Ordner)
 * - Aktionen (Stellungnahme, Überweisung, Aufgabe, etc.)
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface DocAIAktion {
  typ: 'überweisung' | 'aufgabe' | 'frist' | 'prüfung' | 'buchhaltung' | 'weiterleitung' | 'ablage' | 'stellungnahme' | 'akte_ablage' | 'antwort_anwalt';
  titel: string;
  beschreibung: string;
  prioritaet: 'hoch' | 'mittel' | 'niedrig';
  frist?: string;
  betrag?: number;
  empfaenger?: string;
  iban?: string;
  verwendungszweck?: string;
}

export interface DocAIAktenVerknuepfung {
  aktenzeichen?: string;
  kennzeichen?: string;
  schadennummer?: string;
  versicherung?: string;
  gefunden: boolean;
}

export type EmailKategorie = 'stellungnahme' | 'anwalt' | 'versicherung' | 'kunde' | 'werkstatt' | 'mietwagen' | 'autoglas' | 'buchhaltung' | 'intern' | 'sonstiges';

export interface DocAIAnalyse {
  dokumentTyp: string;
  dokumentTypLabel: string;
  firma: string;
  firmaName: string;
  stellungnahmeErforderlich: boolean;
  emailKategorie: EmailKategorie;
  aktenVerknuepfung: DocAIAktenVerknuepfung;
  ablageVorschlag: {
    pfad: string;
    ordnerName: string;
    monat: string;
    jahr: string;
  };
  aktionen: DocAIAktion[];
  metadaten: {
    absender?: string;
    empfaenger?: string;
    datum?: string;
    betrag?: number;
    waehrung?: string;
    rechnungsnummer?: string;
    aktenzeichen?: string;
    kennzeichen?: string;
    schadennummer?: string;
    versicherung?: string;
    pruefdienstleister?: string;
    iban?: string;
    bic?: string;
    verwendungszweck?: string;
    frist?: string;
    anwalt?: string;
    klaeger?: string;
    beklagter?: string;
  };
  zusammenfassung: string;
  konfidenz: number;
}

export function useDocAI() {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DocAIAnalyse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyzeEmail = useCallback(async (
    emailSubject: string,
    emailFrom: string,
    emailBody: string,
    attachmentNames: string[] = [],
    attachmentContent?: string
  ): Promise<DocAIAnalyse | null> => {
    setAnalyzing(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/doc-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: 'analyze',
          emailSubject,
          emailFrom,
          emailBody,
          attachmentNames,
          attachmentContent,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.analysis) {
        setAnalysis(data.analysis);
        return data.analysis;
      }
      throw new Error('Keine Analyse erhalten');
    } catch (err: any) {
      setError(err.message);
      console.error('DocAI Fehler:', err);
      return null;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  return {
    analyzing,
    analysis,
    error,
    analyzeEmail,
    clearAnalysis,
  };
}

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

export function getAktionIcon(typ: DocAIAktion['typ']): string {
  switch (typ) {
    case 'überweisung': return '💳';
    case 'aufgabe': return '📋';
    case 'frist': return '⏰';
    case 'prüfung': return '🔍';
    case 'buchhaltung': return '📊';
    case 'weiterleitung': return '➡️';
    case 'ablage': return '📁';
    case 'stellungnahme': return '⚠️';
    case 'akte_ablage': return '📂';
    case 'antwort_anwalt': return '⚖️';
    default: return '📄';
  }
}

export function getAktionLabel(typ: DocAIAktion['typ']): string {
  switch (typ) {
    case 'überweisung': return 'Überweisung';
    case 'aufgabe': return 'Aufgabe erstellen';
    case 'frist': return 'Frist setzen';
    case 'prüfung': return 'Prüfung erforderlich';
    case 'buchhaltung': return 'Buchhaltung';
    case 'weiterleitung': return 'Weiterleiten';
    case 'ablage': return 'Ablegen';
    case 'stellungnahme': return 'Stellungnahme erstellen';
    case 'akte_ablage': return 'In Akte ablegen';
    case 'antwort_anwalt': return 'Anwalt antworten';
    default: return typ;
  }
}

export function getPrioritaetColor(p: DocAIAktion['prioritaet']): string {
  switch (p) {
    case 'hoch': return '#EF4444';
    case 'mittel': return '#F59E0B';
    case 'niedrig': return '#10B981';
    default: return '#6B7280';
  }
}

export function getDokumentTypIcon(typ: string): string {
  switch (typ) {
    // Gutachter-spezifisch
    case 'kuerzungsschreiben': return '🔴';
    case 'pruefbericht': return '🔴';
    case 'klageerwiderung': return '⚖️';
    case 'stellungnahme_anforderung': return '⚠️';
    case 'anwalt_korrespondenz': return '⚖️';
    case 'versicherung_korrespondenz': return '🛡️';
    case 'abrechnungsschreiben': return '💰';
    case 'nachbesichtigung': return '🔎';
    // Standard
    case 'rechnung': return '🧾';
    case 'rechnung_ausgang': return '📤';
    case 'angebot': return '📝';
    case 'vertrag': return '📜';
    case 'gutachten': return '🔎';
    case 'mahnung': return '⚠️';
    case 'kontoauszug': return '🏦';
    case 'bescheid': return '📋';
    case 'korrespondenz': return '✉️';
    case 'lieferschein': return '📦';
    case 'versicherung': return '🛡️';
    case 'beleg': return '🧾';
    default: return '📄';
  }
}

export function getEmailKategorieLabel(kat: EmailKategorie): string {
  switch (kat) {
    case 'stellungnahme': return 'Stellungnahme / Prüfbericht';
    case 'anwalt': return 'Anwalt';
    case 'versicherung': return 'Versicherung';
    case 'kunde': return 'Kunde';
    case 'werkstatt': return 'Werkstatt';
    case 'mietwagen': return 'Mietwagen';
    case 'autoglas': return 'Autoglas';
    case 'buchhaltung': return 'Buchhaltung';
    case 'intern': return 'Intern';
    case 'sonstiges': return 'Sonstiges';
    default: return kat;
  }
}

export function getEmailKategorieColor(kat: EmailKategorie): string {
  switch (kat) {
    case 'stellungnahme': return '#EF4444'; // rot
    case 'anwalt': return '#8B5CF6'; // lila
    case 'versicherung': return '#3B82F6'; // blau
    case 'kunde': return '#10B981'; // grün
    case 'werkstatt': return '#F97316'; // orange
    case 'mietwagen': return '#06B6D4'; // cyan
    case 'autoglas': return '#14B8A6'; // teal
    case 'buchhaltung': return '#6B7280'; // grau
    case 'intern': return '#9CA3AF'; // hellgrau
    case 'sonstiges': return '#6B7280'; // grau
    default: return '#6B7280';
  }
}
